import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { CenteredSpinner } from '../../../../components/ui/Spinner';
import { ApiError } from '../../../../lib/api';
import { firebaseAuth } from '../../../../lib/firebase';
import { openPortalLibraryEntry } from '../portalApi';
import { PortalShell } from '../PortalShell';
import { LibraryDocumentView, type LibraryDocumentState } from './LibraryDocumentView';
import { usePortalViewerNav } from '../usePortalViewerNav';

// CLIENT PORTAL LIBRARY — one document, opened INSIDE the portal (Round 3).
//
// ⚠️⚠️ THIS PAGE IS THE GATE, AND IT IS OURS: our origin, our React, signed in.
// It asks the server whether this viewer may open this document RIGHT NOW; the
// server re-runs the tile's audience check against the session scope and, only
// on a pass, sets a short-lived portal grant scoped to the document's
// ENTRY-keyed content route. Only THEN does the iframe exist, so its request
// carries the grant. What it renders is LibraryDocumentView — read that file's
// header for the token guard and the sandbox rule.
//
// ⚠️ No caching of the open: every visit asks again (staleTime 0, gcTime 0), so
// a client whose access was removed is refused on their next open rather than
// shown a remembered answer.

export function LibraryDocumentPage() {
  const { entryId = '' } = useParams<{ entryId: string }>();
  const nav = usePortalViewerNav();
  const query = useQuery({
    queryKey: ['portal-library-open', entryId],
    queryFn: () => openPortalLibraryEntry(entryId),
    retry: false,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  });

  const state: LibraryDocumentState = query.isPending
    ? { kind: 'loading' }
    : query.isError
      ? query.error instanceof ApiError && query.error.status === 404
        ? { kind: 'not_available' }
        : { kind: 'error' }
      : query.data.status === 'ok'
        ? { kind: 'open', result: query.data }
        : { kind: 'not_available' };

  return (
    // ⚠️ `fill`: the document gets the viewport height below the header, so
    // there is ONE scrollbar — the document's (Round 5).
    <PortalShell signedInAs={firebaseAuth.currentUser?.email ?? null} nav fill>
      <LibraryDocumentView
        state={state}
        nav={nav}
        onRetry={() => void query.refetch()}
        spinner={<CenteredSpinner />}
      />
    </PortalShell>
  );
}
